import { FC, useMemo } from "react";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  timelineItemClasses
} from "@mui/lab";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { utils } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { RideHistoryItemDto } from "domain/rides/entities/dtos/ride-history.dto";
import {
  getChangeDescription,
  sortHistoryItemsNewestFirst
} from "domain/rides/utils/ride-history.helpers";

export interface RideHistoryTimelineProps {
  items: RideHistoryItemDto[];
  loading?: boolean;
}

const REASON_FIELDS = [
  { field: "changeReason", label: "rideHistory.labels.changeReason" },
  { field: "comments", label: "rideHistory.labels.comments" },
  { field: "detourReason", label: "rideHistory.labels.detourReason" }
] as const;

const RideHistoryTimeline: FC<RideHistoryTimelineProps> = ({
  items,
  loading = false
}) => {
  const { t } = useTranslation(["translation"]);

  const sortedItems = useMemo(
    () => sortHistoryItemsNewestFirst(items),
    [items]
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
        data-testid="ride-history-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (sortedItems.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
        data-testid="ride-history-empty"
      >
        <Typography color="textSecondary">{t("rideHistory.empty")}</Typography>
      </Box>
    );
  }

  return (
    <Timeline
      position="right"
      data-testid="ride-history-timeline"
      sx={{
        m: 0,
        p: 0,
        // Drops the empty opposite-content column MUI reserves on the left.
        [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 }
      }}
    >
      {sortedItems.map((item, index) => {
        const actor = item.changedBy?.displayName?.trim();

        return (
          <TimelineItem
            key={`${item.changedAt}-${index}`}
            data-testid="ride-history-item"
          >
            <TimelineSeparator>
              <TimelineDot color="primary" />
              {index < sortedItems.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ pb: 3 }}>
              <Typography
                variant="subtitle2"
                data-testid="ride-history-item-date"
              >
                {utils.formatDate({
                  date: item.changedAt,
                  dateFormat: "dd MMM yyyy, HH:mm"
                })}
              </Typography>
              {actor && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  data-testid="ride-history-item-actor"
                >
                  {actor}
                </Typography>
              )}
              <Stack
                component="ul"
                spacing={0.5}
                sx={{ listStyle: "none", p: 0, m: 0, mt: 1 }}
              >
                {/* `changes` is nullable in the backend contract. */}
                {(item.changes ?? []).map((change, changeIndex) => (
                  <Typography
                    key={`${change.type}-${changeIndex}`}
                    component="li"
                    variant="body2"
                    data-testid="ride-history-change"
                  >
                    {getChangeDescription(change, t)}
                  </Typography>
                ))}
              </Stack>
              {REASON_FIELDS.map(({ field, label }) =>
                item[field]?.trim() ? (
                  <Typography
                    key={field}
                    variant="body2"
                    mt={1}
                    data-testid={`ride-history-item-${field}`}
                  >
                    <strong>{t(label)}:</strong> {item[field]}
                  </Typography>
                ) : null
              )}
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

export default RideHistoryTimeline;
